import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Layout } from '../components/layout/Layout';
import '../styles/curriculum-manager.css';

function StatCard({ label, value, color, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        flex: 1, minWidth: 140, background: '#fff', border: '1.5px solid #E5E7EB',
        borderRadius: 16, padding: '20px 22px', cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, transform 0.15s',
      }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ fontSize: 28, fontWeight: 900, color: color || '#111827', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#9CA3AF', marginTop: 6 }}>{label}</div>
    </div>
  );
}

function ActionCard({ icon, title, desc, onClick, accent }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 18,
        padding: '24px', cursor: 'pointer', transition: 'box-shadow 0.15s, transform 0.15s, border-color 0.15s',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)';
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.borderColor = accent || '#8B2635';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.borderColor = '#E5E7EB';
      }}
    >
      <div style={{ fontSize: 28 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{title}</div>
      <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}

export const AdminDashboardPage = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({ published: 0, draft: 0, archived: 0, total: 0 });
  const [recentDrafts, setRecentDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'generatedSequencingDecks')),
      getDocs(query(collection(db, 'generatedSequencingDecks'), orderBy('updatedAt', 'desc'), limit(8))),
    ]).then(([allSnap, recentSnap]) => {
      const all = allSnap.docs.map(d => d.data());
      setStats({
        total:     all.length,
        published: all.filter(a => a.status === 'published').length,
        draft:     all.filter(a => a.status === 'draft').length,
        archived:  all.filter(a => a.status === 'archived').length,
      });
      setRecentDrafts(
        recentSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => d.status === 'draft')
      );
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  return (
    <Layout>
      <div className="cm-page">

        <div className="cm-header">
          <div>
            <h1 className="cm-title">Admin Dashboard</h1>
            <p className="cm-subtitle">Manual content workflow for sequencing activities — internal tool, not visible to customers.</p>
          </div>
        </div>

        {/* ── Stats ──────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 32 }}>
          <StatCard label="Total Activities" value={loading ? '—' : stats.total}
            onClick={() => navigate('/admin/curriculum')} />
          <StatCard label="Published" value={loading ? '—' : stats.published} color="#059669"
            onClick={() => navigate('/admin/curriculum')} />
          <StatCard label="Draft" value={loading ? '—' : stats.draft} color="#6B7280"
            onClick={() => navigate('/admin/curriculum')} />
          <StatCard label="Archived" value={loading ? '—' : stats.archived} color="#D1D5DB"
            onClick={() => navigate('/admin/curriculum')} />
        </div>

        {/* ── Actions ───────────────────────────────────────────────── */}
        <h2 style={{ fontSize: 14, fontWeight: 800, color: '#374151', textTransform: 'uppercase',
          letterSpacing: '0.06em', margin: '0 0 14px' }}>
          Content Workflow
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 36 }}>
          <ActionCard
            icon="📋" title="Activity Manager"
            desc="Browse, search, and manage every sequencing activity."
            onClick={() => navigate('/admin/curriculum')}
            accent="#8B2635"
          />
          <ActionCard
            icon="📝" title="Create Activity"
            desc="Add a new activity manually — title, steps, and your own images."
            onClick={() => navigate('/sequencing/generate')}
            accent="#8B2635"
          />
          <ActionCard
            icon="🧩" title="Matching Manager"
            desc="Browse, search, and manage every matching activity."
            onClick={() => navigate('/admin/matching')}
            accent="#8B2635"
          />
          <ActionCard
            icon="🔀" title="Create Matching Activity"
            desc="Add a new matching activity manually — sample, choices, and your own images."
            onClick={() => navigate('/matching/generate')}
            accent="#8B2635"
          />
          <ActionCard
            icon="⚡" title="Generate Matching From Deck"
            desc="Auto-build identical/generalization matching trials from an existing deck's own images."
            onClick={() => navigate('/matching/generate-from-deck')}
            accent="#8B2635"
          />
        </div>

        {/* ── Recent drafts ─────────────────────────────────────────── */}
        {recentDrafts.length > 0 && (
          <>
            <h2 style={{ fontSize: 14, fontWeight: 800, color: '#374151', textTransform: 'uppercase',
              letterSpacing: '0.06em', margin: '0 0 14px' }}>
              Recent Drafts
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentDrafts.map(deck => (
                <div
                  key={deck.id}
                  onClick={() => navigate(`/sequencing/review/${deck.id}`)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12,
                    padding: '14px 18px', cursor: 'pointer', transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#D1D5DB'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#E5E7EB'}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{deck.title || 'Untitled'}</div>
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 2 }}>
                      {deck.category || 'Uncategorized'} · {deck.difficulty} · {deck.stepCount || 0} steps
                    </div>
                  </div>
                  <span style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    background: '#F3F4F6', border: '1.5px solid #D1D5DB', color: '#6B7280',
                  }}>
                    Continue editing →
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </Layout>
  );
};
